import { Box, IconButton, Button, Input, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';

import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../../assets/errors/nodata.png'; // Adjust the path as necessary

const Background = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    image: '',
    page_name: '',
    date_created: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getData('proccess.php','backgrounds'); // Assuming 'backgrounds' is the endpoint or resource name
        if (response.success) {
          setTeams(response.data);
        } else {
          toast.error(response.message || "Failed to load background data");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch backgrounds");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  const handleAddClick = () => {
    setFormData({
      title: '',
      image: '',
      page_name: '',
      date_created: new Date().toISOString().split('T')[0]
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (team) => {
    setSelectedTeam(team);
    setFormData({
      title: team.title || '',
      image: team.image || '',
      page_name: team.page_name || '',
      date_created: team.date_created || new Date().toISOString().split('T')[0]
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (team) => {
    setTeamToDelete(team);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData('proccess.php','backgrounds', teamToDelete.id); // Assuming 'backgrounds' resource and ID
      if (response.success) {
        toast.success(`Background deleted successfully`);
        setTeams(teams.filter(team => team.id !== teamToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete background");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete background");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setTeamToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      image: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (selectedTeam) {
        // Update existing background
        response = await updateData('proccess.php','backgrounds',  formData, selectedTeam.id); // Assuming 'backgrounds' resource
        if (response.success) {
          toast.success(`Background updated successfully`);
          setTeams(response.data);
        }
      } else {
        // Add new background
        response = await addData('proccess.php','backgrounds', formData); // Assuming 'backgrounds' resource
        if (response.success) {
          toast.success(`Background added successfully`);
          setTeams(response.data);
        }
      }
      
      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedTeam(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 20 },
    { field: "title", headerName: "Title", flex: 1, minWidth: 120 },
    { 
      field: "image", 
      headerName: "Background Image", 
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box width={50} height={50}>
          {params.value ? (
            <img
              src={`https://mindsinaction.com.na/assets/media/pictures/${params.value}`}
              alt={params.value || "Module"}
              style={{ width: 50, height: 50, objectFit: "cover" }}
              onClick={() => window.open(`https://mindsinaction.com.na/assets/media/pictures/${params.value}`, "_blank")}
            />
          ) : (
            "No image"
          )}
        </Box>
      )
    },
    { field: "page_name", headerName: "Page Name", flex: 1, minWidth: 120 },
    { field: "date_created", headerName: "Created Date", flex: 1, minWidth: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          <IconButton 
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton 
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.orange[400] }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img 
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No data available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      {/* Add/Edit Dialog */}
      <Dialog 
        open={openEditDialog || openAddDialog} 
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedTeam(null);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>{selectedTeam ? "Edit Background" : "Add New Background"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{width: "100%", padding: "1rem"}}>
            <Box display={"flex"} gap={"1rem"} width={"100%"} mb={"1rem"} flexWrap="wrap">
              <TextField 
                style={{width: "100%"}} 
                id="title" 
                name="title"
                label="Title" 
                variant="standard" 
                value={formData.title}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{width: "100%"}}
                id="page_name"
                name="page_name"
                label="Page Name"
                variant="standard"
                value={formData.page_name}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{width: "100%"}}
                id="date_created"
                name="date_created"
                label="Date Created"
                type="date"
                variant="standard"
                value={formData.date_created}
                onChange={handleFormChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <Box width={"100%"}>
                <label>Background Image</label>
                <Input 
                  type="file" 
                  style={{width: "100%"}} 
                  id="image" 
                  name="image"
                  onChange={handleFileChange}
                  required={!selectedTeam}
                />
              </Box>
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenEditDialog(false);
            setOpenAddDialog(false);
          }}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            sx={{backgroundColor: colors.blue[400]}} 
            variant="contained" 
            endIcon={<SendIcon />}
          >
            {selectedTeam ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{teamToDelete?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm}
            sx={{backgroundColor: colors.red[400]}} 
            variant="contained" 
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent= {"space-between"}>
          <Box flex={1}>
              <Typography
                  variant="h4"
                  color={colors.dark[400]}
                  fontWeight={"bold"}
                  sx={{mb: "5px"}}
              >Background Management</Typography>
              <Typography variant="h5" color={colors.blue[400]}>Manage website backgrounds</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              backgroundColor: colors.blue[400],
              '&:hover': {
                backgroundColor: colors.blue[500],
              }
            }}
          >
            Add Background
          </Button>
      </Box>
      <Divider component="" />
      <Box sx={{ height: "calc(100vh - 250px)", width: "100%" }} backgroundColor = {colors.dark[300]} padding={".2rem"} mt={"1rem"}>
          <DataGrid
            rows={teams}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            sx={{
                border: "none",
                ".MuiDataGrid-cell": {
                borderBottom: "none",
                backgroundColor: "none"
                },
                ".MuiDataGrid-toolbarContainer": {
                padding: "10px",
                backgroundColor: colors.primary[900],
                borderBottom: "none",
                },
                ".MuiDataGrid-quickFilter": {
                padding: "8px",
                borderRadius: "4px",
                border: `1px solid ${colors.primary[100]}`,
                },
                ".MuiDataGrid-columnHeaders": {
                  backgroundColor: colors.primary[900],
                },
                ".MuiDataGrid-footerContainer": {
                  backgroundColor: colors.primary[900],
                },
            }}
            slots={{
                toolbar: () => (
                <div>
                    <GridToolbarQuickFilter />
                </div>
                ),
                noRowsOverlay: NoDataOverlay,
            }}
            slotProps={{
                toolbar: {
                showQuickFilter: true,
                quickFilterProps: {
                    debounceMs: 500,
                    placeholder: "Search...",
                },
                },
            }}
          />
      </Box>
    </Box>
  );
};

export default Background;
